/**
 * Datos estructurados de Schema.org (SEO-03).
 *
 * Son lo que permite que un resultado de Google muestre precio, disponibilidad
 * y migas en vez de solo un titulo azul. Se generan siempre desde el catalogo,
 * nunca a mano (logica-negocio.md, seccion 3.10): un precio escrito a mano se
 * desincroniza y Google penaliza el que no calza con la pagina.
 */
import type { Categoria, FichaProducto, Linea, ProductoResumen } from '@elcarpintero/compartido';

import { SITIO } from '../configuracion/sitio';
import { rutas } from '../utilidades/rutas';
import { urlAbsoluta } from './urls';

/** Un nodo JSON-LD cualquiera. */
export type Esquema = Record<string, unknown>;

/** Un eslabon de las migas de pan. */
export interface Miga {
  nombre: string;
  ruta?: string;
}

/** La ferreteria como negocio con local fisico. Va en la portada. */
export function esquemaOrganizacion(): Esquema {
  return {
    '@context': 'https://schema.org',
    '@type': 'HardwareStore',
    name: SITIO.nombreLargo,
    description: SITIO.descripcion,
    url: urlAbsoluta('/'),
    logo: urlAbsoluta(SITIO.logo),
    telephone: SITIO.telefono,
    email: SITIO.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITIO.direccion.calle,
      addressLocality: SITIO.direccion.comuna,
      addressRegion: SITIO.direccion.region,
      addressCountry: SITIO.direccion.pais,
    },
    sameAs: [SITIO.redes.instagram, SITIO.redes.facebook],
  };
}

/** El sitio con su buscador, para la caja de busqueda en los resultados. */
export function esquemaSitio(): Esquema {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITIO.nombre,
    url: urlAbsoluta('/'),
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: urlAbsoluta('/buscar?termino={search_term_string}'),
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function esquemaMigas(migas: Miga[]): Esquema {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: migas.map((miga, indice) => ({
      '@type': 'ListItem',
      position: indice + 1,
      name: miga.nombre,
      ...(miga.ruta !== undefined ? { item: urlAbsoluta(miga.ruta) } : {}),
    })),
  };
}

/**
 * Producto con su oferta. La disponibilidad sale del estado de stock real: si
 * el producto esta agotado se declara `OutOfStock`, no se esconde el producto.
 */
export function esquemaProducto(ficha: FichaProducto): Esquema {
  const { producto, categoria } = ficha;
  const url = urlAbsoluta(rutas.producto(producto.slug));
  const foto = producto.imagenes[0];

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: producto.nombre,
    sku: producto.sku,
    description: producto.descripcion ?? producto.descripcionSeo ?? producto.nombre,
    category: categoria.nombre,
    url,
    ...(foto !== undefined ? { image: [urlAbsoluta(foto.url)] } : {}),
    ...(producto.marca !== null ? { brand: { '@type': 'Brand', name: producto.marca } } : {}),
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: 'CLP',
      price: producto.precio,
      availability:
        producto.estadoStock === 'disponible'
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: SITIO.nombreLargo },
    },
  };
}

/** Listado de productos de una categoria o linea. */
export function esquemaListaProductos(
  nombre: string,
  productos: ProductoResumen[],
): Esquema {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: nombre,
    numberOfItems: productos.length,
    itemListElement: productos.map((producto, indice) => ({
      '@type': 'ListItem',
      position: indice + 1,
      url: urlAbsoluta(rutas.producto(producto.slug)),
      name: producto.nombre,
    })),
  };
}

/** Migas de una categoria: Inicio > Linea > Categoria. */
export function migasDeCategoria(linea: Linea, categoria: Categoria): Miga[] {
  return [
    { nombre: 'Inicio', ruta: rutas.inicio() },
    { nombre: linea.nombre, ruta: rutas.linea(linea.slug) },
    { nombre: categoria.nombre, ruta: rutas.categoria(categoria.slug) },
  ];
}
